/**
 * MatchListTable — table view of admin match list rows.
 *
 * Each row renders the two participants (with origin badges), a
 * color-coded compatibility score, origin kind, status, and flag
 * indicator. Clicking a row opens the detail panel; clicking the
 * checkbox toggles multi-select for the compare view; clicking a
 * participant's name opens their admin profile (UserProfileLink).
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-matches-summaries-design.md §7.1
 */
import { UserProfileLink } from './UserProfileLink';

export interface MatchListItem {
  match_id: string;
  matched_at: string;
  status: string;
  user1: {
    id: string;
    email: string | null;
    display_name: string | null;
    is_fake: boolean;
    is_tester: boolean;
  };
  user2: {
    id: string;
    email: string | null;
    display_name: string | null;
    is_fake: boolean;
    is_tester: boolean;
  };
  compatibility_score: number | null;
  origin_kind: 'organic' | 'tester_instant' | 'fake_paired' | string;
  has_tester_bypass_artifact: boolean;
  admin_flagged_at: string | null;
  admin_flag_reason: string | null;
  flag_source?: 'admin' | 'safety_rail' | null;
  safety_review_status?: string;
  created_by_admin_id: string | null;
}

interface Props {
  items: MatchListItem[];
  selectedIds: Set<string>;
  openMatchId: string | null;
  multiSelectCap: number;
  onToggleSelect: (id: string) => void;
  onOpenDetail: (id: string) => void;
  /** Active server sort field ('matched_at' | 'score' | null). */
  sortField?: string | null;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string) => void;
}

export function MatchListTable({
  items,
  selectedIds,
  openMatchId,
  multiSelectCap,
  onToggleSelect,
  onOpenDetail,
  sortField = null,
  sortOrder = 'desc',
  onSort,
}: Props) {
  // Inline sortable header — keeps the table's existing inline-style look while
  // adding a server-side sort affordance (▲/▼/↕). Server-side because the list
  // is cursor-paginated: sorting only the loaded rows would be wrong.
  const sortableHeader = (field: string, label: string) => {
    if (!onSort) return <th style={cellHeader}>{label}</th>;
    const active = sortField === field;
    const arrow = !active ? '↕' : sortOrder === 'asc' ? '▲' : '▼';
    return (
      <th style={cellHeader}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSort(field);
          }}
          className={`inline-flex items-center gap-1 ${active ? 'text-foreground' : 'hover:text-foreground'}`}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit' }}
          aria-label={`Sort by ${label}`}
        >
          {label}
          <span style={{ fontSize: '10px', opacity: active ? 1 : 0.4 }}>{arrow}</span>
        </button>
      </th>
    );
  };
  if (items.length === 0) {
    return (
      <div className="mt-3 rounded-md border bg-card p-6 text-center text-muted-foreground">
        No matches match these filters.
      </div>
    );
  }

  return (
    <div className="mt-3 overflow-x-auto rounded-md border bg-card">
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr className="border-b bg-muted/50 text-left text-[13px] text-muted-foreground">
            <th style={cellHeader}> </th>
            {sortableHeader('matched_at', 'Matched at')}
            <th style={cellHeader}>User A</th>
            <th style={cellHeader}>User B</th>
            {sortableHeader('score', 'Score')}
            <th style={cellHeader}>Origin</th>
            <th style={cellHeader}>Status</th>
            <th style={cellHeader}>Flagged</th>
          </tr>
        </thead>
        <tbody>
          {items.map(m => {
            const isOpen = openMatchId === m.match_id;
            const isSelected = selectedIds.has(m.match_id);
            const atCap = !isSelected && selectedIds.size >= multiSelectCap;
            return (
              <tr
                key={m.match_id}
                onClick={() => onOpenDetail(m.match_id)}
                className={`cursor-pointer border-b hover:bg-muted/50 ${isOpen ? 'bg-muted' : ''}`}
              >
                <td
                  style={cell}
                  onClick={e => {
                    e.stopPropagation();
                    if (!atCap) onToggleSelect(m.match_id);
                  }}
                  title={atCap ? `Maximum ${multiSelectCap} matches selectable` : undefined}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={atCap}
                    readOnly
                    aria-label={`Select match ${m.match_id}`}
                  />
                </td>
                <td style={cell}>{formatDate(m.matched_at)}</td>
                <td style={cell}>
                  <UserCell user={m.user1} />
                </td>
                <td style={cell}>
                  <UserCell user={m.user2} />
                </td>
                <td
                  className={`font-semibold ${scoreColorClass(m.compatibility_score)}`}
                  style={cell}
                >
                  {m.compatibility_score ?? '—'}
                </td>
                <td style={cell}>
                  <OriginCell match={m} />
                </td>
                <td style={cell}>
                  <StatusPill status={m.status} />
                </td>
                <td style={cell}>
                  {m.admin_flagged_at
                    ? (m.flag_source === 'safety_rail' ? '⚑ rail' : '⚑')
                    : (m.safety_review_status && m.safety_review_status !== 'not_required' ? m.safety_review_status : '')}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function UserCell({ user }: { user: MatchListItem['user1'] }) {
  const label = user.display_name || user.email || user.id.slice(0, 8);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <UserProfileLink userId={user.id}>{label}</UserProfileLink>
      {user.is_fake && <Badge className="bg-violet-600 text-white">fake</Badge>}
      {user.is_tester && !user.is_fake && (
        <Badge className="bg-sky-500 text-white">tester</Badge>
      )}
    </div>
  );
}

function OriginCell({ match }: { match: MatchListItem }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span>{originLabel(match.origin_kind)}</span>
      {match.created_by_admin_id && (
        <span title="Created by an admin">🔧</span>
      )}
      {match.has_tester_bypass_artifact && (
        <span title="Tester-bypass artifact detected (see PR #1089)">⚠</span>
      )}
    </div>
  );
}

function Badge({
  className,
  children,
}: {
  className: string;
  children: React.ReactNode;
}) {
  return (
    <span
      className={`inline-block rounded-full px-1.5 py-px text-[10px] uppercase tracking-wide ${className}`}
    >
      {children}
    </span>
  );
}

function StatusPill({ status }: { status: string }) {
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs ${statusColorClass(status)}`}
    >
      {status}
    </span>
  );
}

function statusColorClass(status: string): string {
  switch (status) {
    case 'connected':
      return 'bg-green-500/15 text-green-600 dark:text-green-400';
    case 'mutual':
      return 'bg-blue-500/15 text-blue-600 dark:text-blue-400';
    case 'presented':
      return 'bg-amber-500/15 text-amber-600 dark:text-amber-400';
    case 'expired':
      return 'bg-muted text-muted-foreground';
    case 'unmatched':
      return 'bg-red-500/15 text-red-600 dark:text-red-400';
    default:
      return 'bg-muted text-foreground';
  }
}

function originLabel(kind: string) {
  switch (kind) {
    case 'organic':
      return 'Organic';
    case 'tester_instant':
      return 'Tester instant';
    case 'fake_paired':
      return 'Fake-paired';
    default:
      return kind;
  }
}

// Score color thresholds per spec §7.1.
function scoreColorClass(s: number | null): string {
  if (s === null || s === undefined) return 'text-muted-foreground';
  if (s >= 85) return 'text-green-500'; // green
  if (s >= 65) return 'text-amber-500'; // amber
  return 'text-red-500'; // red
}

function formatDate(iso: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

const cellHeader: React.CSSProperties = {
  padding: '10px 12px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const cell: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 14,
  verticalAlign: 'middle',
};
